// client/src/utils/gestureRecognition.js

/**
 * A simplified gesture recognizer that uses random simulation
 * instead of actual ML, to avoid dependency issues
 */
class GestureRecognizer {
    constructor() {
        this.isRunning = false;
        this.currentGesture = 'unknown';
        this.onGestureDetectedCallback = null;
        this.onVelocityCalculatedCallback = null;
        this.onDirectionCalculatedCallback = null;
        this.simulationInterval = null;
        this.gestures = ['flat', 'fist', 'guns', 'unknown'];
    }

    /**
     * Initialize the gesture recognizer
     * @param {HTMLVideoElement} videoElement - Not used in this simulation
     * @returns {Promise<boolean>} Always resolves to true in this simulation
     */
    async initialize(videoElement) {
        console.log('Gesture recognizer initialized in debug mode (simulated gestures)');
        return true;
    }

    /**
     * Register callbacks for gesture events
     * @param {Function} onGestureDetected - Called when a gesture is detected
     * @param {Function} onVelocityCalculated - Called when velocity is calculated
     * @param {Function} onDirectionCalculated - Called when direction is calculated
     */
    registerCallbacks(onGestureDetected, onVelocityCalculated, onDirectionCalculated) {
        this.onGestureDetectedCallback = onGestureDetected;
        this.onVelocityCalculatedCallback = onVelocityCalculated;
        this.onDirectionCalculatedCallback = onDirectionCalculated;
    }

    /**
     * Start tracking gestures (simulation)
     * @returns {boolean} Whether tracking started successfully
     */
    startTracking() {
        this.isRunning = true;
        console.log('Starting simulated gesture tracking');

        // Initially trigger an "unknown" gesture
        if (this.onGestureDetectedCallback) {
            try {
                this.onGestureDetectedCallback('unknown');
            } catch (error) {
                console.error('Error in initial gesture callback:', error);
            }
        }

        // Set up a timer to simulate gesture detection
        this.simulationInterval = setInterval(() => {
            this.simulateGesture();
        }, 2000);

        return true;
    }

    /**
     * Stop tracking gestures
     */
    stopTracking() {
        this.isRunning = false;
        if (this.simulationInterval) {
            clearInterval(this.simulationInterval);
            this.simulationInterval = null;
        }
    }

    /**
     * Simulate a random gesture
     */
    simulateGesture() {
        if (!this.isRunning) return;

        try {
            // Choose a random gesture, with higher probability of keeping the same one
            let newGesture;
            if (Math.random() < 0.4 && this.currentGesture !== 'unknown') {
                newGesture = this.currentGesture;
            } else {
                newGesture = this.gestures[Math.floor(Math.random() * this.gestures.length)];
            }

            this.currentGesture = newGesture;

            // Generate random velocity (10-100)
            const velocity = 10 + Math.random() * 90;

            // Generate random direction
            const angle = Math.random() * Math.PI * 2;
            const direction = {
                x: Math.cos(angle),
                y: Math.sin(angle)
            };

            // Trigger callbacks
            if (this.onGestureDetectedCallback) {
                try {
                    this.onGestureDetectedCallback(newGesture);
                } catch (error) {
                    console.error('Error in gesture detection callback:', error);
                }
            }

            if (this.onVelocityCalculatedCallback) {
                try {
                    this.onVelocityCalculatedCallback(velocity);
                } catch (error) {
                    console.error('Error in velocity calculation callback:', error);
                }
            }

            if (this.onDirectionCalculatedCallback) {
                try {
                    this.onDirectionCalculatedCallback(direction);
                } catch (error) {
                    console.error('Error in direction calculation callback:', error);
                }
            }
        } catch (error) {
            console.error('Error in gesture simulation:', error);
        }
    }
}

export default GestureRecognizer;